/**
 * The application's view of its connection to the server, for the UI to
 * show while work is held up by a network interruption.
 *
 * Two sources feed it. The browser says outright when it is offline. The
 * retry layer (`fetchWithRetry`, and the poll loops' outage tolerance)
 * says when a request is between attempts: a request that failed and is
 * waiting to try again is the surest sign of an interruption the browser
 * did not report, such as a server that cannot be reached over a link
 * that is nominally up.
 *
 * A single retry that succeeds is not an interruption worth announcing —
 * a DHCP renewal fails one attempt and the next lands — so the state
 * turns to `reconnecting` only once a request has been retrying for
 * {@link RECONNECTING_AFTER_MS}, and returns to `connected` as soon as no
 * request is retrying any more.
 *
 * Returning to `connected` does not mean the connection came back: the
 * retrying requests may all have given up on a server still down, or
 * been cancelled. {@link getRecoveryCount} counts the interruptions that
 * ended with the server reached again, so the UI says "restored" only
 * when it is true.
 */

export type ConnectionState = 'connected' | 'reconnecting' | 'offline';

/** How a retrying request settled. */
export type RetryOutcome = 'succeeded' | 'failed' | 'cancelled';

/**
 * How long a request must have been retrying before the state says so,
 * so an interruption the first retry rides out shows nothing.
 */
export const RECONNECTING_AFTER_MS = 2_000;

type Listener = () => void;

const listeners = new Set<Listener>();

/** Requests currently between attempts. */
let retryingRequests = 0;

/** Whether some request has been retrying for {@link RECONNECTING_AFTER_MS}. */
let reconnecting = false;

/** Whether a request has reached the server since the current retrying began. */
let reachedServer = false;

/** Whether the browser has reported offline and not yet online again. */
let wasOffline = browserOffline();

/** Announced interruptions that ended with the server reached again. */
let recoveries = 0;

let reconnectingTimer: ReturnType<typeof setTimeout> | undefined = undefined;

function notify(): void {
    listeners.forEach((listener) => listener());
}

function browserOffline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
}

if (typeof window !== 'undefined') {
    window.addEventListener('offline', () => {
        wasOffline = true;
        notify();
    });
    window.addEventListener('online', () => {
        if (wasOffline) {
            wasOffline = false;
            recoveries += 1;
        }
        notify();
    });
}

/** The current state. */
export function getConnectionState(): ConnectionState {
    if (browserOffline()) {
        return 'offline';
    }
    return reconnecting ? 'reconnecting' : 'connected';
}

/**
 * How many announced interruptions have ended with the connection back:
 * the browser reporting online after offline, or the retrying requests
 * settling with at least one of them having reached the server. A change
 * in the count is the cue to say "restored"; a return to `connected`
 * alone is not, since every retrying request may have given up or been
 * cancelled.
 */
export function getRecoveryCount(): number {
    return recoveries;
}

/**
 * Calls `listener` whenever the state or the recovery count may have
 * changed.
 *
 * @returns Unsubscribes.
 */
export function subscribeConnectionState(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Records that a request has started retrying. Called by the retry layer
 * when a request's first retry is scheduled.
 *
 * @returns Releases the record once the request has settled, with how it
 *     did: `succeeded` once an attempt reached the server, `failed` when
 *     the request gave up, `cancelled` when its caller stopped it. Only a
 *     success counts as the connection coming back. Idempotent.
 */
export function beginRetrying(): (outcome: RetryOutcome) => void {
    retryingRequests += 1;
    if (retryingRequests === 1) {
        reachedServer = false;
        reconnectingTimer = setTimeout(() => {
            reconnectingTimer = undefined;
            reconnecting = true;
            notify();
        }, RECONNECTING_AFTER_MS);
    }
    let released = false;
    return (outcome: RetryOutcome) => {
        if (released) {
            return;
        }
        released = true;
        retryingRequests -= 1;
        if (outcome === 'succeeded') {
            reachedServer = true;
        }
        if (retryingRequests > 0) {
            return;
        }
        clearTimeout(reconnectingTimer);
        reconnectingTimer = undefined;
        if (reconnecting) {
            reconnecting = false;
            if (reachedServer) {
                recoveries += 1;
            }
            notify();
        }
    };
}

/** Returns the store to its initial state. For tests. */
export function resetConnectionState(): void {
    clearTimeout(reconnectingTimer);
    reconnectingTimer = undefined;
    retryingRequests = 0;
    reconnecting = false;
    reachedServer = false;
    wasOffline = browserOffline();
    recoveries = 0;
    notify();
}
